export const RECORDING_POLICY_VERSION = 1;

export const SAFE_RECORDING_PROFILE = Object.freeze({
  resolution: '1080p',
  fps: 30,
  videoBitrate: 6_000_000,
  audioBitrate: 192_000
});

const RECOMMENDED_VIDEO_BITRATES = Object.freeze({
  '720p': Object.freeze({ 30: 4_000_000, 60: 8_000_000 }),
  '1080p': Object.freeze({ 30: 6_000_000, 60: 12_000_000 }),
  '1440p': Object.freeze({ 30: 12_000_000, 60: 20_000_000 }),
  '4k': Object.freeze({ 30: 25_000_000, 60: 35_000_000 })
});

function normalizedFps(value) {
  return Number(value) >= 60 ? 60 : 30;
}

export function getRecommendedVideoBitrate(resolution = '1080p', fps = 30) {
  const selected = RECOMMENDED_VIDEO_BITRATES[String(resolution)]
    || RECOMMENDED_VIDEO_BITRATES['1080p'];
  return selected[normalizedFps(fps)];
}

export function estimateRecordingBytes({
  durationMinutes,
  videoBitrate,
  audioBitrate,
  overheadRatio = 0.05
} = {}) {
  const minutes = Math.max(0, Number(durationMinutes) || 0);
  const bitsPerSecond = Math.max(
    0,
    (Number(videoBitrate) || 0) + (Number(audioBitrate) || 0)
  );
  const overhead = Math.max(0, Number(overheadRatio) || 0);
  return Math.ceil((bitsPerSecond / 8) * minutes * 60 * (1 + overhead));
}

export function applyRecordingPolicyMigration(settings = {}) {
  const current = { ...settings };
  if (Number(current._recordingPolicyVersion) >= RECORDING_POLICY_VERSION) {
    return { settings: current, changed: false };
  }

  if (current.stableRecordingProtection !== false) {
    const resolution = String(current.resolution || SAFE_RECORDING_PROFILE.resolution);
    const fps = Number(current.fps) || SAFE_RECORDING_PROFILE.fps;
    if (resolution === '1080p' && fps <= 30) {
      current.videoBitrate = Math.min(
        Number(current.videoBitrate) || SAFE_RECORDING_PROFILE.videoBitrate,
        SAFE_RECORDING_PROFILE.videoBitrate
      );
      current.audioBitrate = Math.min(
        Number(current.audioBitrate) || SAFE_RECORDING_PROFILE.audioBitrate,
        SAFE_RECORDING_PROFILE.audioBitrate
      );
    }
  }

  current._recordingPolicyVersion = RECORDING_POLICY_VERSION;
  return { settings: current, changed: true };
}

export function selectRecordingRuntimeProfile(settings = {}, environment = {}) {
  const selectedResolution = String(settings.resolution || SAFE_RECORDING_PROFILE.resolution);
  const selectedFps = Math.max(30, Number(settings.fps) || SAFE_RECORDING_PROFILE.fps);
  const selectedBitrate = Number(settings.videoBitrate) || SAFE_RECORDING_PROFILE.videoBitrate;
  const selectedAudioBitrate = Number(settings.audioBitrate) || SAFE_RECORDING_PROFILE.audioBitrate;
  const isAndroid = /Android/i.test(String(environment.userAgent || ''));
  const isAndroidWeb = !environment.isNative && isAndroid;
  const actualFps = Number(environment.actualFps) || 0;
  const highResolution = selectedResolution === '1440p' || selectedResolution === '4k';
  const measuredLowFps = highResolution && actualFps > 0 && actualFps < selectedFps * 0.8;
  const measuredCriticalFps = actualFps > 0 && actualFps < 24;

  if (settings.stableRecordingProtection === false && !isAndroid) {
    return {
      resolution: selectedResolution,
      fps: selectedFps,
      videoBitrate: selectedBitrate,
      audioBitrate: selectedAudioBitrate,
      downshifted: false,
      reason: null
    };
  }

  if (measuredCriticalFps) {
    return {
      resolution: '720p',
      fps: 30,
      videoBitrate: getRecommendedVideoBitrate('720p', 30),
      audioBitrate: Math.min(selectedAudioBitrate, SAFE_RECORDING_PROFILE.audioBitrate),
      downshifted: true,
      reason: 'measured-critical-fps'
    };
  }

  if (isAndroid && highResolution) {
    return {
      ...SAFE_RECORDING_PROFILE,
      downshifted: true,
      reason: isAndroidWeb ? 'android-web-high-resolution' : 'android-high-resolution'
    };
  }

  if (measuredLowFps) {
    return {
      ...SAFE_RECORDING_PROFILE,
      downshifted: true,
      reason: 'measured-low-fps'
    };
  }

  if (isAndroid && selectedFps > 30) {
    return {
      resolution: selectedResolution,
      fps: 30,
      videoBitrate: Math.min(
        selectedBitrate,
        getRecommendedVideoBitrate(selectedResolution, 30)
      ),
      audioBitrate: Math.min(selectedAudioBitrate, SAFE_RECORDING_PROFILE.audioBitrate),
      downshifted: true,
      reason: isAndroidWeb ? 'android-web-high-frame-rate' : 'android-high-frame-rate'
    };
  }

  if (
    settings.stableRecordingProtection !== false
    && selectedResolution === '1080p'
    && selectedFps <= 30
    && (
      selectedBitrate > SAFE_RECORDING_PROFILE.videoBitrate
      || selectedAudioBitrate > SAFE_RECORDING_PROFILE.audioBitrate
    )
  ) {
    return {
      ...SAFE_RECORDING_PROFILE,
      downshifted: true,
      reason: 'stable-recording-bitrate'
    };
  }

  return {
    resolution: selectedResolution,
    fps: selectedFps,
    videoBitrate: selectedBitrate,
    audioBitrate: selectedAudioBitrate,
    downshifted: false,
    reason: null
  };
}
